import React, { useEffect } from "react";
import Action from "../../components/Action";
import Card from "../../components/Card";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { trackQuoteRejection } from "../../utils/analyticsUtils";
import { INSURANCE_LIMIT, TRUSTPILOT } from "../../constants";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";

const QuoteSorryPage = ({ mode, transactionType, rejectionReasons, rejectQuote, showFirst }) => {
  useEffect(() => {
    trackQuoteRejection({ leg: rejectQuote, transactionType, reasons: rejectionReasons });
  }, [rejectQuote, transactionType, rejectionReasons]);

  const findAConveyancer = (
    <p>
      You can find a well-reviewed conveyancer on{" "}
      <a href={TRUSTPILOT.categoryUrl} target="_blank" rel="noopener noreferrer">
        Trustpilot
      </a>
      .
    </p>
  );

  const getInTouch = (
    <p>
      If you have any questions or think we might be able to help after all, give us a call on{" "}
      <a href="tel:02038563339">020 3856 3339</a> — we’re happy to talk it through.
    </p>
  );

  const reasonDescriptions = {
    price_too_high: (
      <React.Fragment key="price_too_high">
        <p>
          As a regulated conveyancing firm, we’re insured to work on transactions up to a value of £
          {INSURANCE_LIMIT / 1000000} million. Unfortunately, we can’t take on cases with a higher value.
        </p>
      </React.Fragment>
    ),
    divorce_or_separation: (
      <React.Fragment key="divorce_or_separation">
        <p>
          We don’t currently act for separating couples selling a property together, because we often receive
          conflicting instructions which mean we have to stop acting mid-way through the sale. In our experience it’s
          better to get a divorce lawyer involved and use their in-house conveyancing specialists for the sale of
          property, who are better equipped to handle situations like this.
        </p>
        <p>
          We recognise this is a difficult time for everyone involved, and we’re sorry that we’re not able to help you
          resolve the situation and move on to the next phase of your life. We wish you all the best for the future.
        </p>
      </React.Fragment>
    ),
    new_build: (
      <React.Fragment key="new_build">
        <p>
          We’re not currently able to help with new build or first registration purchases. These transactions have
          additional complexity that we’re not yet set up to handle.
        </p>
      </React.Fragment>
    ),
    unregistered_land: (
      <React.Fragment key="unregistered_land">
        <p>We’re not currently able to help with {rejectQuote} unregistered land.</p>
      </React.Fragment>
    ),
    auction: (
      <React.Fragment key="auction">
        <p>We’re not currently able to help with {rejectQuote} a property at auction.</p>
      </React.Fragment>
    ),
    company: (
      <React.Fragment key="company">
        <p>We’re not currently set up to act for companies purchasing property.</p>
      </React.Fragment>
    ),
    company_btl: (
      <React.Fragment key="company_btl">
        <p>We’re not currently set up to act for companies purchasing rental properties.</p>
      </React.Fragment>
    ),
    right_to_buy: (
      <React.Fragment key="right_to_buy">
        <p>We’re not currently able to help with right-to-buy purchases.</p>
      </React.Fragment>
    ),
    shared_ownership: (
      <React.Fragment key="shared_ownership">
        <p>We’re not currently able to help with {rejectQuote} a shared ownership property.</p>
      </React.Fragment>
    ),
    retirement_property: (
      <React.Fragment key="retirement_property">
        <p>
          We’re not currently able to help with buying a home built specifically for people in later life (a retirement
          home, sheltered accommodation or an assisted living development).
        </p>
      </React.Fragment>
    ),
    tenure_leasehold: (
      <React.Fragment key="tenure_leasehold">
        <p>We’re not currently able to help with {rejectQuote} a leasehold property.</p>
      </React.Fragment>
    ),
    tenure_freehold: (
      <React.Fragment key="tenure_freehold">
        <p>We’re not currently able to help with {rejectQuote} a freehold property.</p>
      </React.Fragment>
    ),
  };

  // Every reason is recorded above, but over the price limit the others don't matter
  const shownReasons = rejectionReasons.includes("price_too_high") ? ["price_too_high"] : rejectionReasons;

  // Divorce points people to a divorce lawyer instead
  const showFindAConveyancer = shownReasons.some((reason) => reason !== "divorce_or_separation");

  let introText = "We’re really sorry, but this is outside the range of cases we’re currently able to handle.";
  if (shownReasons.includes("divorce_or_separation")) {
    introText = "We’re sorry, we’re not able to help with your planned sale.";
  }

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title="Sorry, we can’t help with this one" removeTopPadding>
        <p>{introText}</p>

        {shownReasons.map((reason) => reasonDescriptions[reason])}

        {showFindAConveyancer && findAConveyancer}

        {getInTouch}

        {mode === "referrer" && <Action onClick={() => showFirst({})}>Refer another client</Action>}
      </Card>
      <Image src={imageOfficeWorking} fullWidth />
    </React.Fragment>
  );
};

export default QuoteSorryPage;
